/**
 * One person's enrolment in a plan, from docs/booking-api.swagger.json.
 *
 * The *plan* is the thing you sell and lives on the Membership Plans page under
 * Maintenance; a row here is a subscription to one. Both were called
 * "Memberships" in the original, which is the naming this page steps away from.
 */

/** `MembershipStatusFilter` — enum [0..4]. */
export const STATUS_FILTER = {
  all: 0,
  active: 1,
  expiring: 2,
  expired: 3,
  cancelled: 4,
} as const;

/** `MembershipPaymentState` — enum [0..6]; the original offered the first six. */
export const PAYMENT_STATE = {
  all: 0,
  paidOnline: 1,
  paidOffline: 2,
  belowList: 3,
  unpaid: 4,
  sponsored: 5,
} as const;

/** `MembershipOrigin` — enum [0..4]. How the person came to have it. */
export const ORIGIN = {
  selfPurchase: 0,
  adminAssigned: 1,
  sponsored: 2,
  gift: 3,
  memberSeat: 4,
} as const;

/** `MembershipDateBasis` — which date the range filters on. */
export const DATE_BASIS = { created: 0, start: 1, end: 2, payment: 3 } as const;

export interface SubscriptionUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  completeName: string | null;
  email: string | null;
  phoneNumber: string | null;
  avatar: string | null;
}

export interface SubscriptionMember {
  id: string;
  userId: string | null;
  completeName: string | null;
  email: string | null;
}

/** `MembershipRowDto`. */
export interface SubscriptionRow {
  id: string;
  createdAt: string | null;
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
  autoRenew: boolean;
  countForLimit: boolean;
  boughtWithPromo: boolean;
  comments: string | null;
  cancelledAt: string | null;
  daysRemaining: number | null;
  isExpiringSoon: boolean;

  user: SubscriptionUser | null;

  subscriptionId: string | null;
  subscriptionNameEn: string | null;
  subscriptionNameEs: string | null;
  subscriptionInternalName: string | null;
  locationNameEn: string | null;
  locationNameEs: string | null;

  listPrice: number | null;
  origin: number | null;
  assignedByName: string | null;

  paidTotal: number | null;
  refundedTotal: number | null;
  paymentCount: number | null;
  lastPaymentAt: string | null;
  hasOfflinePayment: boolean;
  paymentMethods: number[] | null;
  /** Negative when it sold under list price. */
  amountVariance: number | null;

  benefitsUsed: number | null;
  benefitsRemaining: number | null;
  bookingsCount: number | null;

  isMemberSeat: boolean;
  parentUserSubscriptionId: string | null;
  ownerName: string | null;
  members: SubscriptionMember[] | null;
  giftId: string | null;
}

/** `PagedMembershipsDto`. */
export interface PagedSubscriptions {
  total: number;
  page: number;
  size: number;
  memberships: SubscriptionRow[];
}

/**
 * `MembershipSort`. The API defines eight; the original offered six — the two it
 * left out sorted by a start date nobody filters on.
 */
export const SORT = {
  createdDesc: 0,
  createdAsc: 1,
  endingSoonest: 4,
  endingLatest: 5,
  name: 6,
  paidDesc: 7,
} as const;

/** `SubscriptionRecurrence` → how long one period runs. */
export const RECURRENCE_LENGTH: Record<number, [number, 'day' | 'month' | 'year']> = {
  1: [1, 'day'],
  2: [7, 'day'],
  3: [14, 'day'],
  4: [1, 'month'],
  5: [2, 'month'],
  6: [3, 'month'],
  7: [6, 'month'],
  8: [1, 'year'],
};

/** `UserSubscriptionPaymentStatus` — 1 is the only one that means money arrived. */
export const PAYMENT_STATUS = { pending: 0, successful: 1, failed: 2 } as const;

/** `UserSubscriptionPaymentDto`, trimmed to what the drawer reads. */
export interface SubscriptionPayment {
  id: string;
  createdAt: string | null;
  amount: number | null;
  status: number | null;
  refunded: boolean;
  refundedAt: string | null;
  method: number | null;
  /** Recorded by hand rather than taken by a provider — the two are undone differently. */
  isOffline: boolean;
  offlineReference: string | null;
  adminNotes: string | null;
  recordedAt: string | null;
  recordedByName: string | null;
  voidedAt: string | null;
  voidedByName: string | null;
  isRenewal: boolean;
  isFailed: boolean;
}

/** `RemainingBenefitDto`. `limit` null/0 means unlimited, not exhausted. */
export interface RemainingBenefit {
  id: string;
  nameEn: string | null;
  nameEs: string | null;
  internalName: string | null;
  limit: number | null;
  totalLimit: number | null;
  type: number | null;
  amount: number | null;
}

/** `UserBenefitRedemptionDto`. */
export interface BenefitRedemption {
  id: string;
  createdAt: string | null;
  benefit: { nameEn: string | null; nameEs: string | null } | null;
}

/** `MembershipBookingDto`. */
export interface SubscriptionBooking {
  id: string;
  createdAt: string | null;
  timeslotStartTime: string | null;
  timeslotEndTime: string | null;
  eventName: string | null;
  locationName: string | null;
  status: number | null;
  checkedInAt: string | null;
  benefitNameEn: string | null;
  benefitNameEs: string | null;
  /** Value given away against the plan — never money collected. */
  benefitValue: number | null;
}

/** `UserSubscriptionMemberDto` — the other people a multi-seat plan covers. */
export interface SubscriptionMemberRow {
  userSubscriptionId: string | null;
  userId: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  isActive: boolean;
  createdAt: string | null;
}

/** `MembershipDetailDto` — one GET fills every tab of the drawer. */
export interface SubscriptionDetail {
  membership: SubscriptionRow | null;
  payments: SubscriptionPayment[] | null;
  remainingBenefits: RemainingBenefit[] | null;
  redemptions: BenefitRedemption[] | null;
  recentBookings: SubscriptionBooking[] | null;
  members: SubscriptionMemberRow[] | null;
}

/** `WebUserDto`, trimmed to what the create form needs to pick someone. */
export interface UserSearchResult {
  id: string;
  firstName: string | null;
  lastName: string | null;
  completeName: string | null;
  email: string | null;
  phoneNumber: string | null;
  avatar: string | null;
}
